import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HeaderBar from "../widgets/HeaderBar";
import Footer from "../widgets/Footer";
import LayoutContainer from "../widgets/LayoutContainer";

const exchangeRate = 83; // 1 USD to INR

const PricingTiers = () => {
  const theme = useTheme();
  const [currency, setCurrency] = useState("INR");

  const plans = [
    {
      title: "Starter",
      range: "First 500 users",
      price: { USD: 0, INR: 0 },
      features: ["Custom Forms", "Basic Analytics", "Email Support"],
      highlighted: false,
    },
    {
      title: "Growth",
      range: "500 - 1.5K users",
      price: { USD: 49, INR: 49 * exchangeRate },
      features: ["QR Generation", "Advanced Analytics", "Priority Support"],
      highlighted: true,
    },
    {
      title: "Scale",
      range: "1.5K - 5K users",
      price: { USD: 149, INR: 149 * exchangeRate },
      features: ["Premium Entry", "Real-time Dashboard", "Dedicated Support"],
      highlighted: false,
    },
    {
      title: "Enterprise",
      range: "5K - 20K users",
      price: "Custom",
      features: [
        "All Premium Features",
        "SLA",
        "24/7 Support",
        "Custom Integration",
      ],
      highlighted: false,
    },
  ];

  return (
    <LayoutContainer>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: 800, color: theme.palette.text.primary, mb: 6 }}
        >
          Flexible Pricing Plans
        </Typography>

        {/* Currency Toggle */}
        <Box display="flex" justifyContent="center" mb={4}>
          <FormControlLabel
            control={
              <Switch
                checked={currency === "USD"}
                onChange={() => setCurrency(currency === "INR" ? "USD" : "INR")}
                color="primary"
              />
            }
            label={currency === "INR" ? "INR ₹" : "USD $"}
          />
        </Box>

        <Grid container spacing={4}>
          {plans.map((plan, index) => (
            <Grid item key={index} xs={12} sm={6} md={3}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border: plan.highlighted
                    ? `2px solid ${theme.palette.primary.main}`
                    : undefined,
                  boxShadow: plan.highlighted
                    ? theme.shadows[8]
                    : theme.shadows[2],
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: theme.shadows[12],
                  },
                }}
              >
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  {plan.highlighted && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      sx={{ mb: 2, fontWeight: 600, borderRadius: 1 }}
                    />
                  )}

                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 700 }}
                  >
                    {plan.title}
                  </Typography>

                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                    {plan.price === "Custom"
                      ? "Custom"
                      : currency === "INR"
                      ? `₹${plan.price.INR.toLocaleString("en-IN")}`
                      : `$${plan.price.USD}`}
                    {plan.price !== "Free" && plan.price !== "Custom" && (
                      <Typography
                        component="span"
                        variant="body1"
                        color="text.secondary"
                      >
                        /month
                      </Typography>
                    )}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {plan.range}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <List dense>
                    {plan.features.map((feature, fIndex) => (
                      <ListItem key={fIndex} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleRoundedIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: "body1" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <Box sx={{ p: 3 }}>
                  <Button
                    fullWidth
                    variant={plan.highlighted ? "contained" : "outlined"}
                    size="large"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {plan.price === "Free" ? "Get Started" : "Choose Plan"}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Enterprise Section */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            textAlign: "center",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
            Enterprise Scale
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            For organizations with 20K+ users
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Custom solutions with volume discounts, dedicated infrastructure,
            and personalized support.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              mt: 3,
              px: 6,
              borderRadius: 1,
              fontWeight: 600,
            }}
          >
            Contact Sales
          </Button>
        </Box>
      </Container>
    </LayoutContainer>
  );
};

export default PricingTiers;
